import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import AuthModal from "../../pages/subscribe-trial/components/AuthModal";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<"login" | "register">("login");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user, profile, isLoggedIn, loading: authLoading, signOut, getUserIdentifier } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("showLogin") === "1") {
      setAuthMode("login");
      setShowAuthModal(true);
      // Clean the URL param after opening
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("showLogin");
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  interface NavItem {
    label: string;
    path: string;
    icon: string;
  }

  const navLinks: NavItem[] = [
    { label: "首頁", path: "/", icon: "ri-home-3-line" },
    { label: "熱門認購", path: "/shop", icon: "ri-fire-line" },
  ];

  const handleLoginClick = () => {
    setAuthMode("login");
    setShowAuthModal(true);
  };

  const handleRegisterClick = () => {
    setAuthMode("register");
    setShowAuthModal(true);
  };

  const handleLogout = async () => {
    await signOut();
    setDropdownOpen(false);
    navigate("/");
  };

  // Use profile name if available, otherwise fall back to the username part of email
  const emailUsername = getUserIdentifier();
  const userName = profile?.name || emailUsername || "會員";
  const displayName = userName;
  const initials = displayName.slice(0, 2).toUpperCase();
  const isAdmin = profile?.is_admin ?? false;

  // Show username from profile, or from email (stripped suffix), or fallback
  const userIdentifier = profile?.username || emailUsername || "會員";

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/95 backdrop-blur-sm border-b border-stone-200"
            : "bg-stone-900/60 backdrop-blur-sm"
        }`}
      >
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 shrink-0">
              <div className="h-8 w-8 md:h-10 md:w-10 rounded-lg bg-rose-700 flex items-center justify-center shrink-0">
                <i className="ri-star-line text-white text-base md:text-lg" />
              </div>
              <span
                className={`font-display text-lg md:text-xl font-semibold tracking-tight transition-colors ${
                  scrolled ? "text-stone-800" : "text-white"
                }`}
              >
                Star Skull shop
              </span>
            </Link>

            {/* Desktop nav links */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`flex items-center gap-1 font-body text-sm font-medium transition-colors hover:opacity-80 ${
                    scrolled ? "text-stone-700" : "text-white/90"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Desktop right section */}
            <div className="hidden md:flex items-center gap-3">
              <button
                className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors ${
                  scrolled
                    ? "text-stone-700 hover:bg-stone-100"
                    : "text-white/90 hover:bg-white/10"
                }`}
              >
                <i className="ri-search-line text-lg" />
              </button>
              <button
                className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors ${
                  scrolled
                    ? "text-stone-700 hover:bg-stone-100"
                    : "text-white/90 hover:bg-white/10"
                }`}
              >
                <i className="ri-shopping-bag-line text-lg" />
              </button>

              {!authLoading && isLoggedIn ? (
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className={`flex items-center gap-2.5 pl-2 pr-3 py-1.5 rounded-full transition-all ${
                      scrolled
                        ? "hover:bg-stone-100"
                        : "hover:bg-white/10"
                    }`}
                  >
                    <div className="w-8 h-8 rounded-full bg-rose-800 flex items-center justify-center">
                      <span className="font-body text-[11px] font-bold text-white">
                        {initials}
                      </span>
                    </div>
                    <span
                      className={`font-body text-sm font-medium ${
                        scrolled ? "text-stone-700" : "text-white/90"
                      }`}
                    >
                      {displayName}
                    </span>
                    <i
                      className={`ri-arrow-down-s-line text-sm transition-transform ${
                        scrolled ? "text-stone-500" : "text-white/70"
                      } ${dropdownOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  {dropdownOpen && (
                    <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-2xl shadow-xl border border-stone-100 overflow-hidden py-1 animate-fadeIn">
                      <div className="px-4 py-3 border-b border-stone-100">
                        <p className="font-body text-sm font-semibold text-stone-800 truncate">
                          {displayName}
                        </p>
                        <p className="font-body text-[11px] text-stone-400 truncate mt-0.5">
                          {userIdentifier}
                        </p>
                      </div>
                      <Link
                        to="/dashboard#subscriptions"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 font-body text-sm text-stone-700 hover:bg-stone-50 transition-colors"
                      >
                        <i className="ri-dashboard-line text-stone-400" />
                        認購中心
                      </Link>
                      <Link
                        to="/profile"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 font-body text-sm text-stone-700 hover:bg-stone-50 transition-colors"
                      >
                        <i className="ri-user-line text-stone-400" />
                        個人中心
                      </Link>
                      <Link
                        to="/orders"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 font-body text-sm text-stone-700 hover:bg-stone-50 transition-colors"
                      >
                        <i className="ri-file-list-3-line text-stone-400" />
                        我的訂單
                      </Link>
                      {isAdmin && (
                        <Link
                          to="/admin"
                          onClick={() => setDropdownOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 font-body text-sm text-amber-700 hover:bg-amber-50 transition-colors"
                        >
                          <i className="ri-shield-user-line text-amber-500" />
                          後台管理
                        </Link>
                      )}
                      <div className="border-t border-stone-100 mt-1" />
                      <button
                        onClick={handleLogout}
                        className="flex w-full items-center gap-3 px-4 py-2.5 font-body text-sm text-stone-500 hover:text-rose-700 hover:bg-rose-50 transition-colors"
                      >
                        <i className="ri-logout-box-r-line" />
                        登出
                      </button>
                    </div>
                  )}
                </div>
              ) : authLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-24 h-9 rounded-full bg-white/20 animate-pulse" />
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleLoginClick}
                    className={`font-body text-sm font-medium px-4 py-2 rounded-full transition-colors whitespace-nowrap ${
                      scrolled
                        ? "text-stone-700 hover:bg-stone-100"
                        : "text-white/90 hover:bg-white/10"
                    }`}
                  >
                    登入
                  </button>
                  <button
                    onClick={handleRegisterClick}
                    className="font-body text-sm font-semibold px-5 py-2 rounded-full bg-rose-800 text-white hover:bg-rose-900 transition-colors whitespace-nowrap"
                  >
                    註冊
                  </button>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden w-9 h-9 flex items-center justify-center"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              <i
                className={`text-xl transition-colors ${
                  scrolled ? "text-stone-800" : "text-white"
                } ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}
              />
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-stone-200">
            <div className="px-4 py-4 flex flex-col gap-3">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="font-body text-sm font-medium text-stone-700 py-2"
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className="border-t border-stone-100 pt-3 flex flex-col gap-2">
                {!authLoading && isLoggedIn ? (
                  <>
                    <Link
                      to="/dashboard#subscriptions"
                      className="font-body text-sm font-medium text-stone-700 py-2 flex items-center gap-2"
                      onClick={() => setMobileOpen(false)}
                    >
                      <i className="ri-dashboard-line" />
                      認購中心
                    </Link>
                    <Link
                      to="/profile"
                      className="font-body text-sm font-medium text-stone-700 py-2 flex items-center gap-2"
                      onClick={() => setMobileOpen(false)}
                    >
                      <i className="ri-user-line" />
                      個人中心
                    </Link>
                    <Link
                      to="/orders"
                      className="font-body text-sm font-medium text-stone-700 py-2 flex items-center gap-2"
                      onClick={() => setMobileOpen(false)}
                    >
                      <i className="ri-file-list-3-line" />
                      我的訂單
                    </Link>
                    {isAdmin && (
                      <Link
                        to="/admin"
                        className="font-body text-sm font-medium text-amber-700 py-2 flex items-center gap-2"
                        onClick={() => setMobileOpen(false)}
                      >
                        <i className="ri-shield-user-line" />
                        後台管理
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        handleLogout();
                        setMobileOpen(false);
                      }}
                      className="font-body text-sm font-medium text-rose-700 py-2 flex items-center gap-2"
                    >
                      <i className="ri-logout-box-r-line" />
                      登出
                    </button>
                  </>
                ) : authLoading ? (
                  <div className="py-2">
                    <div className="h-9 w-full rounded-full bg-stone-100 animate-pulse" />
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setMobileOpen(false);
                        handleLoginClick();
                      }}
                      className="font-body text-sm font-semibold py-2.5 rounded-full border border-stone-300 text-stone-700 text-center"
                    >
                      登入
                    </button>
                    <button
                      onClick={() => {
                        setMobileOpen(false);
                        handleRegisterClick();
                      }}
                      className="font-body text-sm font-semibold px-5 py-2.5 rounded-full bg-rose-800 text-white text-center"
                    >
                      註冊
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultMode={authMode}
      />
    </>
  );
}